import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { selectAdminToken } from '../../admin/sessionSlice';
import { s3PublicUrl } from '../../utils/s3PublicUrl';
import {
  completeStageRevealNavigation,
  isDemoSubscribeRedirectUser,
  runDemoStageSubmitFlow,
  scheduleStagePostSubmitNavigation,
} from '../../utils/demoGate';
import { mergeGameAssignmentIntoUserData } from '../../utils/gameStages';
import { buildCumulativeStageTimes, buildCumulativeStageScore, readCumulativeScoreFromProgress, readCumulativeTimeFromProgress } from '../../utils/stageReportTime';
import { resolvePostSubmitNavigation } from '../../utils/stageNavigation';
import {
  fetchGameStages,
  initStageReport,
  updateStageProgress,
} from '../../functions/stageReport';
import { sendReport } from '../../functions/sendReport';
import { buildStagePageStyle, stageThemeVisuals } from '../../utils/stageThemeVisuals';
import StagePuzzleReveal from '../../components/StagePuzzleReveal/StagePuzzleReveal';
import { primeDictionaryWordCache, validateDictionaryWord } from '../../utils/dictionaryValidation';
import {
  buildCategoryAnswersMap,
  buildCategoryRejectedMap,
  countStage3CorrectWords,
  shouldValidateStage3WordWithDictionary,
  validateStage3RelatedWord,
} from '../../utils/stage3WordValidation';
import '../../components/StagePuzzleReveal/stageLayout.css';
import './category-rush.css';
import {
  StagePlayGate,
  useStageAlreadyPlayedGuard,
} from '../../utils/useStageAlreadyPlayedGuard';
import { useStageBackButton } from '../../utils/useStageBackButton';
import { getStageRulesList, useStageRulesModal } from '../../utils/useStageRulesModal';

const STAGE_NUMBER = 3;
const RELATED_WORD_MAX = 100;
const POINTS_PER_CORRECT_WORD = 1;
const STAGE3_SESSION_PREFIX = 'stage3_session';
const GAME_DISPLAY_TITLE = 'CATEGORY RUSH';

function stage3SessionKey(userId, sessionId) {
  return `${STAGE3_SESSION_PREFIX}_${userId}_${sessionId}`;
}

function loadStage3Session(userId, sessionId) {
  if (!userId || !sessionId) return null;
  try {
    const raw = sessionStorage.getItem(stage3SessionKey(userId, sessionId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function saveStage3Session(userId, sessionId, data) {
  if (!userId || !sessionId) return;
  try {
    sessionStorage.setItem(stage3SessionKey(userId, sessionId), JSON.stringify(data));
  } catch {
    /* ignore */
  }
}

function clearStage3Session(userId, sessionId) {
  if (!userId || !sessionId) return;
  try {
    sessionStorage.removeItem(stage3SessionKey(userId, sessionId));
  } catch {
    /* ignore */
  }
}

function getStage3StorageIds(user) {
  const userId = user?.userId;
  const sessionId = user?.sessionId;
  if (!userId || !sessionId) return null;
  return { userId: String(userId), sessionId: String(sessionId) };
}

function isStage3PromptValid(promptWord, wordList) {
  const prompt = String(promptWord ?? '').trim().toLowerCase();
  if (!prompt) return false;
  return wordList.some((word) => String(word ?? '').trim().toLowerCase() === prompt);
}

function buildStage3SessionSnapshot({
  startTime,
  durationSeconds,
  promptWord,
  relatedWords = [],
  timerExpired = false,
}) {
  return {
    startTime,
    durationSeconds,
    promptWord,
    relatedWords,
    ...(timerExpired ? { timerExpired: true } : {}),
  };
}

function parseStageTimerSeconds(raw, fallback = 180) {
  const s = String(raw ?? '').trim();
  if (!s) return fallback;
  if (s.includes(':')) {
    const parts = s.split(':').map((p) => Math.floor(Number(p)) || 0);
    if (parts.length === 2) return parts[0] * 60 + parts[1];
    if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  }
  const n = Math.floor(Number(s));
  return Number.isFinite(n) && n > 0 ? n : fallback;
}

function formatCountdown(totalSeconds) {
  const safe = Math.max(0, Math.floor(totalSeconds));
  const minutes = Math.floor(safe / 60);
  const seconds = safe % 60;
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
}

function formatStageDescription(raw) {
  return String(raw ?? '')
    .replace(/<br\s*\/?>/gi, '\n')
    .trim();
}

function collectStage3Items(stage3) {
  const fromQuestions = Array.isArray(stage3?.questions) ? stage3.questions : [];
  if (fromQuestions.length) return fromQuestions;
  const fromTopics = Array.isArray(stage3?.topics) ? stage3.topics : [];
  if (fromTopics.length) return fromTopics;
  return Array.isArray(stage3?.words) ? stage3.words : [];
}

function normalizeTopicWords(items) {
  const seen = new Set();
  const out = [];
  for (const q of Array.isArray(items) ? items : []) {
    const raw =
      typeof q === 'string' || typeof q === 'number'
        ? String(q)
        : String(q?.word ?? q?.topic ?? q?.question ?? '');
    const word = raw.replace(/\s+/g, ' ').trim();
    if (!word) continue;
    const key = word.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(word);
  }
  return out;
}

function collectThemeAnswerWords(stage3Items) {
  const words = [];
  for (const item of Array.isArray(stage3Items) ? stage3Items : []) {
    if (!item || typeof item !== 'object') continue;
    const topic = String(item.word ?? item.topic ?? item.question ?? '').trim();
    if (topic) words.push(topic);
    for (const answer of Array.isArray(item.answers) ? item.answers : []) {
      const a = String(answer ?? '').trim();
      if (a) words.push(a);
    }
  }
  return words;
}

function ClockIcon() {
  return (
    <svg className="s3-timer-icon" viewBox="0 0 24 24" aria-hidden>
      <path
        fill="currentColor"
        d="M12 2a10 10 0 1 0 10 10A10.011 10.011 0 0 0 12 2Zm0 18a8 8 0 1 1 8-8 8.009 8.009 0 0 1-8 8Zm.5-13H11v6l4.7 2.8.8-1.3-4-2.4Z"
      />
    </svg>
  );
}

export default function Stage3({ onStageComplete }) {
  const navigate = useNavigate();
  useStageBackButton(3);
  const themeData = useSelector((state) => state.theme.data);
  const user = useSelector((state) => state.auth?.user);
  const adminToken = useSelector(selectAdminToken);
  const stage3 = themeData?.stages_data?.stage_3;

  const logoUrl = s3PublicUrl(stage3?.logo ?? stage3?.badge);
  const rulesList = getStageRulesList(stage3);
  const subtitle = String(stage3?.title ?? stage3?.name ?? 'Think Fast. Write Faster.');
  const descriptionText = useMemo(
    () => formatStageDescription(stage3?.description),
    [stage3?.description]
  );

  const addWordsSeconds = useMemo(
    () => parseStageTimerSeconds(stage3?.timer, 180),
    [stage3?.timer]
  );

  const stage3Items = useMemo(() => collectStage3Items(stage3), [stage3]);

  const wordList = useMemo(() => normalizeTopicWords(stage3Items), [stage3Items]);

  const categoryAnswersMap = useMemo(
    () => buildCategoryAnswersMap(stage3Items),
    [stage3Items]
  );

  const categoryRejectedMap = useMemo(
    () => buildCategoryRejectedMap(stage3Items),
    [stage3Items]
  );

  const isConfigured =
    !!stage3 &&
    String(stage3?.title ?? stage3?.name ?? '').trim().length > 0 &&
    wordList.length > 0;

  const visuals = useMemo(() => stageThemeVisuals(themeData), [themeData]);
  const pageStyle = useMemo(() => buildStagePageStyle(visuals, 's3'), [visuals]);

  const completeSentRef = useRef(false);
  const revealTimeoutRef = useRef(null);
  const nextPathRef = useRef('/puzzle');
  const timerRef = useRef(null);
  const autoSubmitRef = useRef(false);
  const handleSubmitRef = useRef(null);
  const sessionStartRef = useRef(null);
  const sessionDurationRef = useRef(null);
  const relatedInputRef = useRef(null);

  const [promptWord, setPromptWord] = useState('');
  const [relatedInput, setRelatedInput] = useState('');
  const [relatedWords, setRelatedWords] = useState([]);
  const [secondsLeft, setSecondsLeft] = useState(addWordsSeconds);
  const [timerExpired, setTimerExpired] = useState(false);
  const [sessionReady, setSessionReady] = useState(false);
  const [message, setMessage] = useState('');
  const [inputShake, setInputShake] = useState(false);
  const [validatingWord, setValidatingWord] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showRevealOverlay, setShowRevealOverlay] = useState(false);
  const [revealCompletedDays, setRevealCompletedDays] = useState(null);

  const { playCheckReady, alreadyPlayed, beginSubmit } = useStageAlreadyPlayedGuard({
    stageNumber: STAGE_NUMBER,
    submitted,
    user,
    adminToken,
    navigate,
  });

  const { showRules, openRules, closeRules } = useStageRulesModal({
    stageNumber: STAGE_NUMBER,
    rulesList,
    isConfigured,
    submitted,
    alreadyPlayed,
    userId: user?.userId,
    sessionId: user?.sessionId,
    ready: !!stage3,
  });

  useEffect(() => {
    setLogoFailed(false);
  }, [logoUrl]);

  useEffect(() => {
    primeDictionaryWordCache([...wordList, ...collectThemeAnswerWords(stage3Items)]);
  }, [wordList, stage3Items]);

  useEffect(
    () => () => {
      if (revealTimeoutRef.current) {
        window.clearTimeout(revealTimeoutRef.current);
        revealTimeoutRef.current = null;
      }
    },
    []
  );

  useEffect(() => {
    if (!isConfigured || submitted || alreadyPlayed || !playCheckReady || !wordList.length) return undefined;

    const storageIds = getStage3StorageIds(user);
    if (!storageIds) return undefined;
    const { userId, sessionId } = storageIds;

    const now = Date.now();
    let session = loadStage3Session(userId, sessionId);
    const promptValid = isStage3PromptValid(session?.promptWord, wordList);
    const storedDuration = Number(session?.durationSeconds);
    const roundDuration =
      Number.isFinite(storedDuration) && storedDuration > 0 ? storedDuration : addWordsSeconds;

    if (!session?.startTime || !promptValid) {
      session = buildStage3SessionSnapshot({
        startTime: now,
        durationSeconds: addWordsSeconds,
        promptWord: wordList[Math.floor(Math.random() * wordList.length)],
        relatedWords: [],
      });
      saveStage3Session(userId, sessionId, session);
    }

    sessionStartRef.current = session.startTime;
    sessionDurationRef.current = roundDuration;
    setPromptWord(session.promptWord);
    setRelatedWords(
      Array.isArray(session.relatedWords)
        ? session.relatedWords.filter((w) => String(w ?? '').trim())
        : []
    );

    if (session.timerExpired) {
      setSecondsLeft(0);
      setTimerExpired(true);
      setSessionReady(true);
      return undefined;
    }

    const elapsed = Math.floor((now - session.startTime) / 1000);
    const remaining = Math.max(0, roundDuration - elapsed);
    setSecondsLeft(remaining);
    setTimerExpired(remaining === 0);
    setSessionReady(true);

    if (timerRef.current) {
      window.clearInterval(timerRef.current);
    }

    if (remaining > 0) {
      timerRef.current = window.setInterval(() => {
        const elapsedNow = Math.floor((Date.now() - session.startTime) / 1000);
        const left = Math.max(0, roundDuration - elapsedNow);
        setSecondsLeft(left);
        if (left === 0) {
          setTimerExpired(true);
          saveStage3Session(
            userId,
            sessionId,
            buildStage3SessionSnapshot({
              startTime: session.startTime,
              durationSeconds: roundDuration,
              promptWord: session.promptWord,
              relatedWords: session.relatedWords || [],
              timerExpired: true,
            })
          );
          if (timerRef.current) {
            window.clearInterval(timerRef.current);
            timerRef.current = null;
          }
        }
      }, 1000);
    } else {
      saveStage3Session(
        userId,
        sessionId,
        buildStage3SessionSnapshot({
          startTime: session.startTime,
          durationSeconds: roundDuration,
          promptWord: session.promptWord,
          relatedWords: session.relatedWords || [],
          timerExpired: true,
        })
      );
    }

    return () => {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
    };
  }, [addWordsSeconds, alreadyPlayed, isConfigured, playCheckReady, submitted, user, user?.userId, user?.sessionId, wordList]);

  useEffect(() => {
    if (!sessionReady || !timerExpired || submitted || alreadyPlayed || !playCheckReady) return;
    if (autoSubmitRef.current) return;
    autoSubmitRef.current = true;
    handleSubmitRef.current?.(null, { auto: true });
  }, [sessionReady, timerExpired, submitted, alreadyPlayed, playCheckReady]);

  const persistRelatedWords = (words) => {
    const storageIds = getStage3StorageIds(user);
    if (!storageIds || !sessionStartRef.current || !promptWord) return;
    const { userId, sessionId } = storageIds;
    saveStage3Session(
      userId,
      sessionId,
      buildStage3SessionSnapshot({
        startTime: sessionStartRef.current,
        durationSeconds: sessionDurationRef.current ?? addWordsSeconds,
        promptWord,
        relatedWords: words,
        timerExpired,
      })
    );
  };

  const triggerShake = () => {
    setInputShake(true);
    window.setTimeout(() => setInputShake(false), 500);
  };

  const validateAndPrepareWordSync = (rawWord) => {
    const word = String(rawWord ?? '').trim();
    if (!word) {
      return { ok: false, message: 'Enter a related word', shake: true };
    }
    const key = word.toLowerCase();
    if (relatedWords.some((w) => w.toLowerCase() === key)) {
      return { ok: false, message: 'You already added that word', shake: false };
    }
    const validation = validateStage3RelatedWord(word, promptWord, {
      allTopics: wordList,
      themeAnswersMap: categoryAnswersMap,
      rejectedAnswersMap: categoryRejectedMap,
    });
    if (!validation.valid) {
      return { ok: false, message: validation.message, shake: true, clearInput: true };
    }
    return { ok: true, word };
  };

  const validateAndPrepareWordAsync = async (rawWord) => {
    const syncResult = validateAndPrepareWordSync(rawWord);
    if (!syncResult.ok) return syncResult;

    if (
      !shouldValidateStage3WordWithDictionary(promptWord, categoryAnswersMap)
    ) {
      return syncResult;
    }

    const dictionaryResult = await validateDictionaryWord(syncResult.word);
    if (!dictionaryResult.valid) {
      return {
        ok: false,
        message: dictionaryResult.message,
        shake: true,
        clearInput: true,
      };
    }
    return syncResult;
  };

  const handleAddRelatedWord = async (e) => {
    e?.preventDefault?.();
    if (timerExpired || validatingWord) {
      if (timerExpired) setMessage("Time's up — you can no longer add words");
      return;
    }
    setValidatingWord(true);
    setMessage('Checking dictionary…');
    try {
      const result = await validateAndPrepareWordAsync(relatedInput);
      if (!result.ok) {
        if (result.shake) triggerShake();
        setMessage(result.message);
        if (result.clearInput) setRelatedInput('');
        return;
      }
      setRelatedWords((prev) => {
        const next = [...prev, result.word];
        persistRelatedWords(next);
        return next;
      });
      setRelatedInput('');
      setMessage('');
      window.setTimeout(() => relatedInputRef.current?.focus(), 0);
    } finally {
      setValidatingWord(false);
    }
  };

  const handleRemoveWord = (wordToRemove) => {
    if (timerExpired || submitted) return;
    setRelatedWords((prev) => {
      const next = prev.filter((w) => w !== wordToRemove);
      persistRelatedWords(next);
      return next;
    });
    if (message) setMessage('');
  };

  const handleSubmit = async (e, { auto = false } = {}) => {
    if (e?.preventDefault) e.preventDefault();
    if (submitted) return;

    let words = relatedWords;
    const pending = String(relatedInput ?? '').trim();
    if (
      !timerExpired &&
      pending &&
      !words.some((w) => w.toLowerCase() === pending.toLowerCase())
    ) {
      setValidatingWord(true);
      setMessage('Checking dictionary…');
      let pendingResult;
      try {
        pendingResult = await validateAndPrepareWordAsync(pending);
      } finally {
        setValidatingWord(false);
      }
      if (!pendingResult.ok) {
        if (pendingResult.shake) triggerShake();
        setMessage(pendingResult.message);
        if (pendingResult.clearInput) setRelatedInput('');
        return;
      }
      words = [...words, pendingResult.word];
    }

    if (!words.length && !timerExpired && !auto) {
      triggerShake();
      setMessage('Add at least one related word');
      return;
    }

    const userId = user?.userId;
    const organizationId = user?.organizationId;
    const sessionId = user?.sessionId;
    const token =
      adminToken ||
      (sessionId && organizationId ? `${sessionId}&${organizationId}` : null);

    if (!token || !userId || !organizationId || !sessionId) {
      setMessage('Unable to save your response. Please sign in again.');
      return;
    }

    const correctWordCount = countStage3CorrectWords(words, promptWord, {
      allTopics: wordList,
      themeAnswersMap: categoryAnswersMap,
      rejectedAnswersMap: categoryRejectedMap,
    });
    const finalPoints = correctWordCount * POINTS_PER_CORRECT_WORD;
    const trimmedAnswer = JSON.stringify({
      prompt: promptWord,
      related: words,
    });

    if (isDemoSubscribeRedirectUser(user)) {
      if (timerRef.current) {
        window.clearInterval(timerRef.current);
        timerRef.current = null;
      }
      runDemoStageSubmitFlow({
        user,
        navigate,
        revealTimeoutRef,
        nextPathRef,
        beginSubmit,
        markSubmitted: () => setSubmitted(true),
        setMessage,
        setRevealCompletedDays,
        setShowRevealOverlay,
        completedDaysForReveal: STAGE_NUMBER,
      });
      if (typeof onStageComplete === 'function' && !completeSentRef.current) {
        completeSentRef.current = true;
        onStageComplete({
          earnedPoints: finalPoints,
          answer: trimmedAnswer,
          allCorrect: correctWordCount > 0 && correctWordCount === words.length,
        });
      }
      return;
    }

    const elapsedSeconds = sessionStartRef.current
      ? Math.min(
          sessionDurationRef.current ?? addWordsSeconds,
          Math.floor((Date.now() - sessionStartRef.current) / 1000)
        )
      : sessionDurationRef.current ?? addWordsSeconds;

    setMessage('');
    beginSubmit();
    setSubmitted(true);

    if (timerRef.current) {
      window.clearInterval(timerRef.current);
      timerRef.current = null;
    }

    try {
      await initStageReport({
        token,
        userId,
        email: user?.email,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
        totalPuzzles: themeData?.total_puzzles ?? 9,
        timeSeconds: elapsedSeconds,
      });

      const stageData = await fetchGameStages({ token, userId, organizationId, sessionId });
      const game = stageData?.game ?? null;
      const nextDay = stageData?.nextDay != null ? Number(stageData.nextDay) : null;
      if (!Number.isFinite(nextDay) || nextDay < 1) {
        throw new Error('No active day found for this stage');
      }

      mergeGameAssignmentIntoUserData({
        game,
        currentStage: stageData?.currentStage,
        nextDay: nextDay + 1,
      });

      const reportTimes = buildCumulativeStageTimes(stageData, elapsedSeconds);
      const reportScores = buildCumulativeStageScore(stageData, finalPoints);

      const progressResult = await updateStageProgress({
        token,
        userId,
        dayNumber: STAGE_NUMBER,
        points: finalPoints,
        timeSeconds: reportTimes.currentTimeSeconds,
        answer: trimmedAnswer,
        allCorrect: correctWordCount > 0 && correctWordCount === words.length,
        email: user?.email,
        name: user?.name,
      });

      sendReport({
        token,
        userId,
        email: user?.email,
        points: readCumulativeScoreFromProgress(progressResult) ?? reportScores.cumulativeScore,
        accumulatePoints: false,
        time: readCumulativeTimeFromProgress(progressResult) ?? reportTimes.cumulativeTimeSeconds,
        name: user?.name,
        userType: user?.role || user?.source || 'ORG_USER',
        reportId: user?.reportId || '',
        themeName: 'Big Picture',
      });

      clearStage3Session(userId, sessionId);
    } catch (err) {
      setSubmitted(false);
      autoSubmitRef.current = false;
      setMessage(err?.message || 'Failed to save your response. Please try again.');
      return;
    }

    let path = '/puzzle';
    let completedDaysForReveal = 1;
    try {
      const { path: nextPath, completedDaysForReveal: revealDays } =
        await resolvePostSubmitNavigation({
          stageNumber: STAGE_NUMBER,
          token,
          userId,
          organizationId,
          sessionId,
        });
      path = nextPath;
      completedDaysForReveal = revealDays;
    } catch {
      path = '/puzzle';
    }

    if (typeof onStageComplete === 'function' && !completeSentRef.current) {
      completeSentRef.current = true;
      onStageComplete({
        earnedPoints: finalPoints,
        answer: trimmedAnswer,
        allCorrect: correctWordCount > 0 && correctWordCount === words.length,
      });
    }

    nextPathRef.current = path;
    setRevealCompletedDays(completedDaysForReveal);
    setShowRevealOverlay(true);
    scheduleStagePostSubmitNavigation({
      user,
      navigate,
      path,
      revealTimeoutRef,
    });
  };

  handleSubmitRef.current = handleSubmit;

  const revealConfettiPieces = useMemo(
    () =>
      Array.from({ length: 20 }, (_, i) => ({
        id: i,
        left: `${Math.round((i / 19) * 100)}%`,
        delay: `${(i % 7) * 0.08}s`,
        duration: `${2.1 + (i % 5) * 0.22}s`,
      })),
    []
  );

  const handleRevealOverlayClick = (ev) => {
    if (ev.target !== ev.currentTarget) return;
    completeStageRevealNavigation({
      user,
      navigate,
      path: nextPathRef.current || '/puzzle',
      revealTimeoutRef,
    });
  };

  const timerLabel = sessionReady
    ? timerExpired
      ? '00:00'
      : formatCountdown(secondsLeft)
    : formatCountdown(addWordsSeconds);

  return (
    <div className="stage3-page" style={pageStyle}>
      <StagePlayGate
        playCheckReady={playCheckReady}
        alreadyPlayed={alreadyPlayed}
        isConfigured={isConfigured}
        notConfiguredMessage={
          <p className="main-day-subtitle">
            Stage 3 is not configured: set stage_3 title and at least one topic word.
          </p>
        }
      >
        <div className="stage-page-main stage3-main">
            <header className="s3-top-bar">
              <div
                className={`s3-top-timer${timerExpired ? ' s3-top-timer--expired' : ''}`}
                aria-label="Stage timer"
                aria-live="polite"
                role="timer"
              >
                <ClockIcon />
                <span className="s3-timer-value">{timerLabel}</span>
              </div>
              {rulesList.length > 0 ? (
                <button
                  type="button"
                  className="stage3-rules-fab"
                  onClick={openRules}
                  aria-label="How to play"
                  title="How to play"
                >
                  ?
                </button>
              ) : null}
            </header>

            <div className="s3-heading">
              <div className="s3-game-title">
                {logoUrl && !logoFailed ? (
                  <img
                    src={logoUrl}
                    alt={GAME_DISPLAY_TITLE}
                    className="s3-game-logo"
                    onError={() => setLogoFailed(true)}
                  />
                ) : (
                  <h1 className="s3-game-title-text">{GAME_DISPLAY_TITLE}</h1>
                )}
              </div>
              {subtitle ? <p className="s3-game-subtitle">{subtitle}</p> : null}
              {descriptionText && !subtitle ? (
                <p className="s3-game-subtitle">{descriptionText}</p>
              ) : null}
            </div>

            <div className="s3-game-row">
            <div className="s3-play-area">
              <div className="s3-category-row">
                <div className="s3-category-box">
                  <span className="s3-category-label">Category</span>
                  <strong className="s3-category-word">{promptWord}</strong>
                  <span className="s3-lightning-deco" aria-hidden>
                    <i className="fa-solid fa-bolt" />
                  </span>
                </div>
              </div>

              {!submitted ? (
                <form className="s3-answers-form" onSubmit={handleSubmit}>
                  <div className="s3-answers-panel">
                    {relatedWords.length > 0 ? (
                      <div className="s3-answer-chips" role="list" aria-label="Your answers">
                        {relatedWords.map((word) => (
                          <span key={word} className="s3-answer-chip" role="listitem">
                            <span className="s3-answer-chip-text">{word}</span>
                            {!timerExpired ? (
                              <button
                                type="button"
                                className="s3-answer-chip-remove"
                                onClick={() => handleRemoveWord(word)}
                                aria-label={`Remove ${word}`}
                              >
                                ×
                              </button>
                            ) : null}
                          </span>
                        ))}
                      </div>
                    ) : null}

                    {!timerExpired ? (
                      <div className="s3-answer-input-row">
                        <input
                          ref={relatedInputRef}
                          id="stage3-related"
                          type="text"
                          className={`s3-answer-input${inputShake ? ' s3-answer-input--shake' : ''}`}
                          value={relatedInput}
                          onChange={(ev) => {
                            setRelatedInput(ev.target.value.slice(0, RELATED_WORD_MAX));
                            if (message) setMessage('');
                          }}
                          onKeyDown={(ev) => {
                            if (ev.key === 'Enter') {
                              ev.preventDefault();
                              handleAddRelatedWord();
                            }
                          }}
                          maxLength={RELATED_WORD_MAX}
                          placeholder="Type an answer and press Enter…"
                          disabled={submitted || timerExpired || validatingWord}
                          aria-label="Answer for this category"
                          aria-busy={validatingWord}
                        />
                        <button
                          type="button"
                          className="s3-answer-add-btn"
                          onClick={handleAddRelatedWord}
                          disabled={
                            submitted ||
                            timerExpired ||
                            validatingWord ||
                            !String(relatedInput ?? '').trim()
                          }
                        >
                          {validatingWord ? 'Checking…' : 'Add'}
                        </button>
                      </div>
                    ) : null}
                  </div>

                  <button
                    type="submit"
                    className="s3-submit-btn"
                    disabled={
                      submitted ||
                      validatingWord ||
                      (!timerExpired &&
                        relatedWords.length === 0 &&
                        !String(relatedInput ?? '').trim())
                    }
                  >
                    Submit
                  </button>
                </form>
              ) : (
                <p className="s3-complete">Thanks! Your response has been submitted.</p>
              )}

              {message ? <div className="s3-hint">{message}</div> : null}
            </div>

            <div className="stage-page-puzzle-aside stage3-puzzle-aside">
              <StagePuzzleReveal themeData={themeData} />
            </div>
            </div>
        </div>
      </StagePlayGate>

      {showRules && rulesList.length > 0 ? (
        <div
          className="stage3-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="stage3-rules-title"
          onClick={closeRules}
        >
          <div className="stage3-modal" onClick={(ev) => ev.stopPropagation()}>
            <h2 id="stage3-rules-title">How to play</h2>
            <ul className="stage3-rules-list">
              {rulesList.map((line, i) => (
                <li key={i}>{line}</li>
              ))}
            </ul>
            <div className="stage3-modal-actions">
              <button type="button" onClick={closeRules}>
                Close
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {showRevealOverlay ? (
        <div
          className="stage-reveal-overlay"
          role="dialog"
          aria-modal="true"
          aria-label="Revealing your puzzle progress"
          onClick={handleRevealOverlayClick}
        >
          <div className="stage-reveal-confetti" aria-hidden>
            {revealConfettiPieces.map((piece) => (
              <span
                key={piece.id}
                className="stage-reveal-confetti-piece"
                style={{
                  left: piece.left,
                  animationDelay: piece.delay,
                  animationDuration: piece.duration,
                }}
              />
            ))}
          </div>
          <div className="stage-reveal-stage">
            <aside className="stage-reveal-panel" aria-label="Puzzle pieces progress">
              <StagePuzzleReveal
                themeData={themeData}
                completedDaysOverride={revealCompletedDays}
                animateReveal
              />
            </aside>
          </div>
        </div>
      ) : null}
    </div>
  );
}
